import { useState, useCallback } from 'react'
import type { VenueDocument } from '@/types/venue'
import { Button } from '@/components/ui/button'
import { Upload, X, FileText, FileCheck } from 'lucide-react'

interface DocumentUploaderProps {
  documents: VenueDocument[]
  onChange: (documents: VenueDocument[]) => void
  onUpload: (file: File) => Promise<VenueDocument>
  disabled?: boolean // Disable uploads (e.g., for new unsaved venues)
  disabledMessage?: string // Message to show when disabled
}

export function DocumentUploader({ documents, onChange, onUpload, disabled, disabledMessage }: DocumentUploaderProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)

  const isDisabled = disabled || uploading

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const isValidDocument = (file: File) => {
    return (
      file.type === 'application/pdf' ||
      file.type === 'text/plain' ||
      file.type === 'text/markdown' ||
      file.type === 'text/csv' ||
      file.name.endsWith('.txt') ||
      file.name.endsWith('.md') ||
      file.name.endsWith('.csv')
    )
  }

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (disabled) return

    const files = Array.from(e.dataTransfer.files).filter(isValidDocument)

    if (files.length > 0) {
      setUploading(true)
      try {
        const newDocs = await Promise.all(files.map((file) => onUpload(file)))
        onChange([...documents, ...newDocs])
      } catch (error) {
        console.error('Upload failed:', error)
      } finally {
        setUploading(false)
      }
    }
  }, [documents, onChange, onUpload, disabled])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const validFiles = Array.from(files).filter(isValidDocument)

    if (validFiles.length > 0) {
      setUploading(true)
      try {
        const newDocs = await Promise.all(validFiles.map((file) => onUpload(file)))
        onChange([...documents, ...newDocs])
      } catch (error) {
        console.error('Upload failed:', error)
      } finally {
        setUploading(false)
      }
    }
  }, [documents, onChange, onUpload])

  const removeDocument = (docId: string) => {
    onChange(documents.filter((d) => d.id !== docId))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">RAG Documents</h3>
        <span className="text-sm text-gray-500">
          {documents.length} document{documents.length !== 1 ? 's' : ''}
        </span>
      </div>

      <p className="text-sm text-gray-600">
        Upload PDFs or text files containing information about your restaurant (menus, FAQs, policies, etc.)
        to enhance the AI chatbot's knowledge.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDrop={isDisabled ? undefined : handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDisabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <FileText className={`h-12 w-12 mx-auto mb-4 ${isDisabled ? 'text-gray-300' : 'text-gray-400'}`} />
        <p className={`mb-2 ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
          {disabled && disabledMessage
            ? disabledMessage
            : 'Drag and drop PDFs or text files here, or click to select'}
        </p>
        <input
          type="file"
          accept=".pdf,.txt,.md,.csv,text/plain,text/markdown,text/csv,application/pdf"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="document-upload"
          disabled={isDisabled}
        />
        <label htmlFor="document-upload">
          <Button type="button" variant="outline" disabled={isDisabled} asChild>
            <span>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Select Documents'}
            </span>
          </Button>
        </label>
      </div>

      {/* Document list */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {doc.processed ? (
                  <FileCheck className="h-5 w-5 text-green-500 flex-shrink-0" />
                ) : (
                  <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{doc.filename}</p>
                  <p className="text-xs text-gray-500">
                    {doc.fileType.toUpperCase()} • {doc.processed ? 'Processed' : 'Processing...'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeDocument(doc.id)}
                className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
              >
                <X className="h-4 w-4 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
